import { useRef, type Ref } from 'react';
import { useIntl } from 'react-intl';
import { useMergeRefs, useAutoSelectSingleOption } from '@/shared/hooks';
import { useCountries } from './hooks';
import type { CountryOption } from './types';

interface CountrySelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  countries: CountryOption[];
  isLoading?: boolean;
  placeholder?: string;
  loadingText?: string;
  autoSelectSingle?: boolean;
  preferredValue?: string;
  ref?: Ref<HTMLSelectElement>;
}

type ConnectedProps = Omit<CountrySelectProps, 'countries' | 'isLoading'>;

function CountrySelect({
  ref: forwardedRef,
  countries,
  isLoading,
  placeholder,
  loadingText,
  autoSelectSingle = true,
  preferredValue,
  ...props
}: CountrySelectProps) {
  const intl = useIntl();
  const localRef = useRef<HTMLSelectElement>(null);
  const ref = useMergeRefs(localRef, forwardedRef);

  useAutoSelectSingleOption({
    ref: localRef,
    options: countries,
    enabled: autoSelectSingle,
    getValue: (c) => c.code,
    preferredValue,
  });

  const placeholderText = placeholder ?? intl.formatMessage({ id: 'shared.countrySelect.placeholder' });
  const loadingTextResolved = loadingText ?? intl.formatMessage({ id: 'shared.countrySelect.loading' });

  return (
    <select ref={ref} {...props}>
      <option value="">{isLoading ? loadingTextResolved : placeholderText}</option>
      {countries.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name} ({c.code})
        </option>
      ))}
    </select>
  );
}

function OriginCountrySelect(props: ConnectedProps) {
  const { data, isLoading } = useCountries();

  return <CountrySelect countries={data?.origin_countries ?? []} isLoading={isLoading} {...props} />;
}

export { OriginCountrySelect };
